import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProducts,
  createStockItem,
  getStockList,
  getLastStockItemForEachVariety,
  getCategories,
  getProviders,
  createProvider,
  uploadFile,
  addVarietyToProduct,
} from "../api"; // Replace with actual API functions
import ProviderFormFields from "../components/providers/ProviderFormFields";
import StockProductPickerPanel from "../components/StockProductPickerPanel";
import { formatRut } from "../utils/rut";
import InlineLoading from "../components/InlineLoading";
import PriceItem from "../components/PriceItem";
import {
  EMPTY_PROVIDER,
  createEmptyAccount,
  getProviderApiErrorMessage,
  validateProviderForm,
} from "../utils/provider";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { normalizePaginatedResponse } from "../utils/pagination";
import { resolvePriceClientType } from "../utils/clientType";
import { notify } from '../utils/alertBus';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';

// TODO: Hacer review de que lo que se compró efectivamente llegó (checklist e impresión)
const STATIC_CATEGORIES = [
  { id: "chama-fruit", label: "Frutas y Verduras" },
  { id: "chama-max", label: "Construcción" },
  { id: "chama-todo", label: "Otros Alimentos" },
];

const StockCreatePage = () => {
  const { id } = useParams();
  const [modalVariety, setModalVariety] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState("");
  const [lastStockItems, setLastStockItems] = useState({});
  const [stockListVarietyCounts, setStockListVarietyCounts] = useState({});
  const [quantity, setQuantity] = useState("");
  const [stockPrice, setStockPrice] = useState("");
  const [metricUnit, setMetricUnit] = useState("");
  const [weight, setWeight] = useState("");
  const [bultosPerBin, setBultosPerBin] = useState("");
  const [providers, setProviders] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [providerSearchTerm, setProviderSearchTerm] = useState("");
  const [isProviderSelectOpen, setIsProviderSelectOpen] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [providerForm, setProviderForm] = useState(EMPTY_PROVIDER);
  const [providerAccounts, setProviderAccounts] = useState([]);
  const [providerError, setProviderError] = useState("");
  const [isSavingProvider, setIsSavingProvider] = useState(false);
  const [isSavingStock, setIsSavingStock] = useState(false);
  const [payments, setPayments] = useState([]);
  const [modalProduct, setModalProduct] = useState(null);
  const [showNewVarietyModal, setShowNewVarietyModal] = useState(false);
  const [newVarietyName, setNewVarietyName] = useState("");
  const [newVarietyImage, setNewVarietyImage] = useState(null);
  const [newVarietyFile, setNewVarietyFile] = useState(null);
  const [newVarietyDescription, setNewVarietyDescription] = useState("");
  const branchId =
    useParams().branchId || localStorage.getItem("selectedBranchId");
  const { user } = useCurrentUser();
  const canReadProducts = hasPermission(user, "product.read");
  const canUpdateProduct = hasPermission(user, "product.update");
  const canReadStock = hasPermission(user, "stock.read");
  const canCreateStock = hasPermission(user, "stock.create");
  const canReadProvider = hasPermission(user, "provider.read");
  const canCreateProvider = hasPermission(user, "provider.create");

  const displayCategories = categories.length ? categories : STATIC_CATEGORIES;

  const resolveProductCategoryId = (product) => {
    if (Number.isInteger(product?.categoryId)) return product.categoryId;
    const raw = product?.category;
    if (!raw) return null;
    const normalized = String(raw).trim().toLowerCase();
    const match = displayCategories.find((category) => {
      const byId = String(category?.id ?? "").trim().toLowerCase() === normalized;
      if (byId) return true;
      const label = String(category?.name ?? category?.label ?? "").trim().toLowerCase();
      return label === normalized;
    });
    return match?.id ?? null;
  };

  const resolveCategoryLabel = (category) =>
    category?.name || category?.label || String(category?.id ?? "");

  const [forcedVariety, setForcedVariety] = useState(null);
  const [forcedProductId, setForcedProductId] = useState(null);
  const providerSelectRef = useRef(null);

  const navigate = useNavigate();
  const isBinUnit = (value) => String(value || "").trim().toLowerCase() === "bin";
  const paymentMethods = [
    { id: "efectivo", label: "Efectivo" },
    { id: "transferencia", label: "Transferencia" },
    { id: "tarjeta", label: "Tarjeta" },
    { id: "cheque", label: "Cheque" },
  ];
  const today = new Date().toISOString().split("T")[0];

  const fetchProviders = useCallback(async () => {
    try {
      if (!canReadProvider) {
        setAllProviders([]);
        setProviders([]);
        return;
      }
      const data = await getProviders({ includeDeleted: true, pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      const providerRows = normalized.data || [];
      setAllProviders(providerRows);
      setProviders(providerRows.filter((provider) => !provider.isDeleted));
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  }, [canReadProvider]);

  const fetchPrices = useCallback(async () => {
    try {
      if (!canReadProducts) {
        setProducts([]);
        setFilteredProducts([]);
        return;
      }
      const data = await getProducts(branchId, { pageSize: 0 });
      const normalizedProducts = normalizePaginatedResponse(data);
      setProducts(normalizedProducts.data || []);
      if (canReadStock) {
        const stockData = await getLastStockItemForEachVariety(branchId, { pageSize: 0 });
        const normalizedStock = normalizePaginatedResponse(stockData);
        const stockItemsMap = (normalizedStock.data || []).reduce((acc, stockItem) => {
          acc[stockItem.varietyId] = stockItem;
          return acc;
        }, {});
        setLastStockItems(stockItemsMap);
      } else {
        setLastStockItems({});
      }
    } catch (error) {
      console.error("Error fetching prices:", error);
    }
  }, [branchId, canReadProducts, canReadStock]);

  const fetchStockListVarietyStatus = useCallback(async () => {
    if (!branchId || !id || !canReadStock) {
      setStockListVarietyCounts({});
      return;
    }
    try {
      const stockListData = await getStockList(branchId, id);
      const counts = (stockListData?.Stocks || []).reduce((acc, stockItem) => {
        const varietyId = Number(stockItem?.varietyId);
        if (!Number.isInteger(varietyId)) return acc;
        acc[varietyId] = (acc[varietyId] || 0) + 1;
        return acc;
      }, {});
      setStockListVarietyCounts(counts);
    } catch (error) {
      console.error("Error fetching stock list variety status:", error);
    }
  }, [branchId, id, canReadStock]);

  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) {
      fetchPrices();
      return;
    }
    if (nextProducts === products) return;
    setProducts(nextProducts);
  }, [products, fetchPrices]);

  useProductEvents(branchId, handleProductEvent);

  useEffect(() => {
    fetchPrices();
    fetchProviders();
    fetchStockListVarietyStatus();
  }, [fetchPrices, fetchProviders, fetchStockListVarietyStatus]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!branchId) return;
      setCategoriesLoading(true);
      setCategoriesError("");
      try {
        const data = await getCategories({ branchId });
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
        setCategoriesError("No se pudieron cargar las categorías.");
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, [branchId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        providerSelectRef.current &&
        !providerSelectRef.current.contains(event.target)
      ) {
        setIsProviderSelectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleProviderFormChange = (e) => {
    const { name, value } = e.target;
    setProviderForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProviderRutChange = (e) => {
    setProviderForm((prev) => ({ ...prev, rut: formatRut(e.target.value) }));
  };

  const handleProviderAccountChange = (index, field, value) => {
    setProviderAccounts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddProviderAccount = () => {
    if (!canCreateProvider) return;
    setProviderAccounts((prev) => [...prev, createEmptyAccount()]);
  };

  const handleRemoveProviderAccount = (account, index) => {
    if (!canCreateProvider) return;
    if (!account.id) {
      setProviderAccounts((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    setProviderAccounts((prev) => prev.filter((_, i) => i !== index));
  };

  const resetProviderForm = () => {
    setProviderForm(EMPTY_PROVIDER);
    setProviderAccounts([]);
    setProviderError("");
  };

  const handleProviderModalClose = () => {
    setShowProviderModal(false);
    resetProviderForm();
  };

  const handleSaveProvider = async (e) => {
    e.preventDefault();
    if (!canCreateProvider) return;
    setProviderError("");
    const {
      payload: providerPayload,
      accountPayloads,
      error: validationError,
    } = validateProviderForm({
      form: providerForm,
      accounts: providerAccounts,
      providers: allProviders,
    });
    if (validationError) {
      setProviderError(validationError);
      return;
    }
    setIsSavingProvider(true);
    try {
      const payload = {
        ...providerPayload,
        bankAccounts: accountPayloads.map(({ id: _, ...accountPayload }) => accountPayload),
      };
      const created = await createProvider(payload);
      const nextAllProviders = [...allProviders, created].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
      const nextProviders = nextAllProviders.filter((provider) => !provider.isDeleted);
      setAllProviders(nextAllProviders);
      setProviders(nextProviders);
      setSelectedProviderId(created.id);
      setShowProviderModal(false);
      resetProviderForm();
    } catch (error) {
      console.error("Error creating provider:", error);
      setProviderError(getProviderApiErrorMessage(error, "Error al crear proveedor."));
    } finally {
      setIsSavingProvider(false);
    }
  };

  const handleAddPaymentRow = () => {
    if (!canCreateStock) return;
    setPayments((prev) => [
      ...prev,
      { amount: "", method: "efectivo", reference: "", paidAt: today, voucherFile: null },
    ]);
  };

  const handlePaymentChange = (index, field, value) => {
    setPayments((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handlePaymentVoucherChange = (index, file) => {
    setPayments((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], voucherFile: file || null };
      return next;
    });
  };

  const handleRemovePaymentRow = (index) => {
    setPayments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreateVariety = async () => {
    if (!canUpdateProduct) return;
    if (!newVarietyName) {
      notify("Por favor, ingresa el nombre de la variedad.");
      return;
    }

    try {
      let uploadedImageUrl = null;

      if (newVarietyFile) {
        const response = await uploadFile(newVarietyFile);
        uploadedImageUrl = response.url;
      }

      const newVariety = {
        varietyName: newVarietyName,
        varietyImage: uploadedImageUrl,
        description: newVarietyDescription, // Include description
        productId: modalProduct.id,
      };

      const createdVariety = await addVarietyToProduct(branchId, newVariety);

      // Update the product with the new variety
      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === modalProduct.id
            ? { ...product, Varieties: [...product.Varieties, createdVariety] }
            : product
        )
      );

      setModalProduct((prevProduct) => ({
        ...prevProduct,
        Varieties: [...prevProduct.Varieties, createdVariety],
      }));

      // Close Modal and Reset Form
      setShowNewVarietyModal(false);
      setNewVarietyName("");
      setNewVarietyImage(null);
      setNewVarietyFile(null);
      setNewVarietyDescription(""); // Reset description
    } catch (error) {
      console.error("Error creating variety:", error);
    }
  };

  const openForcedPriceUpdate = (variety, productId) => {
    if (!variety) return;
    setForcedVariety(variety);
    setForcedProductId(productId ?? null);
  };

  const closeForcedPriceUpdate = () => {
    setForcedVariety(null);
    setForcedProductId(null);
  };

  const handleModalClose = () => {
    setModalVariety(null);
    setQuantity("");
    setStockPrice("");
    setMetricUnit("");
    setWeight("");
    setBultosPerBin("");
    setPayments([]);
    setProviderSearchTerm("");
    setIsProviderSelectOpen(false);
  };

  const handleMetricUnitChange = (value) => {
    setMetricUnit(value);
    if (!isBinUnit(value)) {
      setBultosPerBin("");
      return;
    }
    if (!bultosPerBin) {
      const lastValue = lastStockItems?.[modalVariety?.id]?.lastBinBultos;
      if (lastValue) {
        setBultosPerBin(String(lastValue));
      }
    }
  };

  const handleSave = async () => {
    if (isSavingStock) return;
    if (!canCreateStock) return;
    if (!quantity || !stockPrice) {
      notify("Por favor, completa todos los campos.");
      return;
    }
    if (!selectedProviderId) {
      notify("Selecciona un proveedor.");
      return;
    }

    const totalAmount = Number(stockPrice) * Number(quantity);
    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
      notify("El total debe ser mayor a 0.");
      return;
    }

    const validatedPayments = [];
    for (let i = 0; i < payments.length; i++) {
      const payment = payments[i];
      const amount = Number(payment.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        notify(`Monto invalido en el pago ${i + 1}.`);
        return;
      }
      if (!payment.method) {
        notify(`Metodo de pago obligatorio en el pago ${i + 1}.`);
        return;
      }
      validatedPayments.push({
        amount,
        method: payment.method,
        reference: normalizeValue(payment.reference),
        paidAt: payment.paidAt || undefined,
        voucherFile: payment.voucherFile,
      });
    }

    const paymentTotal = validatedPayments.reduce((sum, item) => sum + item.amount, 0);
    if (paymentTotal > totalAmount) {
      notify("El total de pagos no puede superar el total de la compra.");
      return;
    }

    const isBin = isBinUnit(metricUnit);
    const bultosPerBinValue = Number(bultosPerBin);
    if (isBin) {
      if (!Number.isInteger(bultosPerBinValue) || bultosPerBinValue <= 0) {
        notify("Ingresa los bultos por bin.");
        return;
      }
    }

    setIsSavingStock(true);
    try {
      const normalizedPayments = [];
      for (let i = 0; i < validatedPayments.length; i++) {
        const payment = validatedPayments[i];
        let voucherUrl;
        if (payment.voucherFile) {
          try {
            const response = await uploadFile(payment.voucherFile);
            voucherUrl = response?.url;
          } catch (error) {
            console.error("Error subiendo comprobante:", error);
            notify(`Error al subir comprobante en el pago ${i + 1}.`);
            return;
          }
        }
        const paymentPayload = {
          amount: payment.amount,
          method: payment.method,
          reference: payment.reference,
          paidAt: payment.paidAt,
        };
        if (voucherUrl) {
          paymentPayload.voucherUrl = voucherUrl;
        }
        normalizedPayments.push(paymentPayload);
      }
      await createStockItem(branchId, {
        price: stockPrice,
        quantity,
        varietyId: modalVariety.id,
        varietyName: modalVariety.varietyName,
        varietyImage: modalVariety.varietyImage,
        metricUnit,
        weight,
        bultosPerBin: isBin ? bultosPerBinValue : undefined,
        providerId: selectedProviderId,
        payments: normalizedPayments,
        stockListId: id,
      });
      setStockListVarietyCounts((prev) => ({
        ...prev,
        [modalVariety.id]: (prev[modalVariety.id] || 0) + 1,
      }));
      // Close purchase modal
      handleModalClose();

      // Get up-to-date variety object (from current product modal or fallback)
      const v =
        modalProduct?.Varieties?.find((vv) => vv.id === modalVariety.id) ||
        modalVariety;
      // Open the forced price editor if allowed
      if (canUpdateProduct) {
        openForcedPriceUpdate(v, modalProduct?.id);
      }
    } catch (error) {
      console.error("Error al crear el stock:", error);
    } finally {
      setIsSavingStock(false);
    }
  };

  const handleForcedVarietyUpdate = (updatedVariety) => {
    if (!updatedVariety?.id) return;
    setProducts((prev) =>
      prev.map((product) => {
        const matchById =
          forcedProductId !== null && forcedProductId !== undefined
            ? String(product.id) === String(forcedProductId)
            : (product.Varieties || []).some(
                (variety) => variety.id === updatedVariety.id
              );
        if (!matchById) return product;
        return {
          ...product,
          Varieties: (product.Varieties || []).map((variety) =>
            variety.id === updatedVariety.id
              ? { ...variety, ...updatedVariety }
              : variety
          ),
        };
      })
    );

    setModalProduct((prev) => {
      if (!prev) return prev;
      const matchById =
        forcedProductId !== null && forcedProductId !== undefined
          ? String(prev.id) === String(forcedProductId)
          : (prev.Varieties || []).some(
              (variety) => variety.id === updatedVariety.id
            );
      if (!matchById) return prev;
      return {
        ...prev,
        Varieties: (prev.Varieties || []).map((variety) =>
          variety.id === updatedVariety.id
            ? { ...variety, ...updatedVariety }
            : variety
        ),
      };
    });

    setForcedVariety((prev) =>
      prev?.id === updatedVariety.id ? { ...prev, ...updatedVariety } : prev
    );
  };

  const totalAmount = Number(stockPrice || 0) * Number(quantity || 0);
  const safeTotalAmount = Number.isFinite(totalAmount) ? totalAmount : 0;
  const paidAmount = payments.reduce((sum, payment) => {
    const amount = Number(payment.amount || 0);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  const balanceAmount = Math.max(safeTotalAmount - paidAmount, 0);
  const overpayAmount = Math.max(paidAmount - safeTotalAmount, 0);
  const normalizedProviderSearch = providerSearchTerm.trim().toLowerCase();
  const selectedProviderKey = selectedProviderId
    ? String(selectedProviderId)
    : "";
  const filteredProviders = providers.filter((provider) => {
    const providerId = String(provider.id);
    if (providerId === selectedProviderKey) return true;
    if (!normalizedProviderSearch) return true;
    const name = provider.name?.toLowerCase() || "";
    const rut = provider.rut?.toLowerCase() || "";
    const giro = provider.giro?.toLowerCase() || "";
    return (
      name.includes(normalizedProviderSearch) ||
      rut.includes(normalizedProviderSearch) ||
      giro.includes(normalizedProviderSearch)
    );
  });
  const selectedProvider =
    selectedProviderKey &&
    providers.find((provider) => String(provider.id) === selectedProviderKey);
  const selectedProviderLabel = selectedProvider?.name || "Selecciona un proveedor";
  const providerSelectDisabled = !canCreateStock || !canReadProvider;
  const forcedProduct =
    forcedProductId !== null && forcedProductId !== undefined
      ? products.find(
          (product) => String(product.id) === String(forcedProductId)
        )
      : modalProduct;
  const activeForcedVariety =
    forcedProduct?.Varieties?.find(
      (variety) => variety.id === forcedVariety?.id
    ) || forcedVariety;

  return (
    <>
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Title */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Registro de Compras</h1>
            <button
              onClick={() => navigate(`/branches/${branchId}/stock`)}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Volver
            </button>
          </div>

          {!canReadProducts ? (
            <p className="text-sm text-gray-600">
              No tienes permisos para ver productos.
            </p>
          ) : (
            <StockProductPickerPanel
              products={products}
              categories={displayCategories}
              categoriesLoading={categoriesLoading}
              categoriesError={categoriesError}
              resolveProductCategoryId={resolveProductCategoryId}
              resolveCategoryLabel={resolveCategoryLabel}
              onSelectVariety={(variety, product) => {
                setModalProduct(product);
                setModalVariety({
                  ...variety,
                  productName: product?.productName || variety?.productName,
                });
              }}
              getVarietyBadge={(variety) => {
                const addedCount = Number(stockListVarietyCounts[variety.id] || 0);
                const isAlreadyInList = addedCount > 0;
                return {
                  label: isAlreadyInList
                    ? `Agregado${addedCount > 1 ? ` x${addedCount}` : ""}`
                    : "Faltante",
                  cardClassName: isAlreadyInList
                    ? "bg-emerald-50/70 border-emerald-300 ring-1 ring-emerald-200"
                    : "bg-amber-50/70 border-amber-300",
                  badgeClassName: isAlreadyInList
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700",
                };
              }}
              renderVarietyReference={(variety, context) => {
                const wholesalePrices = (context?.sellablePrices || []).filter(
                  (price) =>
                    resolvePriceClientType(price) === "mayorista"
                    && Number.isFinite(Number(price.price))
                );
                const wholesaleReference =
                  wholesalePrices.length > 0
                    ? [...wholesalePrices].sort(
                        (entryA, entryB) =>
                          Number(entryA.price) - Number(entryB.price)
                      )[0]
                    : null;
                const lastPurchase = lastStockItems[variety.id] || null;
                return (
                  <>
                    {context?.isVarietySoldOut ? (
                      <p className="text-red-600 text-xs font-semibold mt-1">
                        AGOTADO
                      </p>
                    ) : wholesaleReference ? (
                      <p className="text-slate-600 text-xs mt-1">
                        Mayorista ref: $
                        {Number(wholesaleReference.price || 0).toLocaleString("de-DE")}{" "}
                        {wholesaleReference.metricUnit || ""}
                      </p>
                    ) : (
                      <p className="text-slate-500 text-xs mt-1">
                        Sin precio mayorista
                      </p>
                    )}
                    <p className="text-slate-500 text-xs mt-1">
                      Última compra:{" "}
                      {lastPurchase
                        ? `$${Number(lastPurchase.price || 0).toLocaleString("de-DE")}`
                        : "Sin registro"}
                    </p>
                  </>
                );
              }}
              renderProductAction={({ product }) =>
                canUpdateProduct ? (
                  <button
                    className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800"
                    onClick={() => {
                      setModalProduct(product);
                      setShowNewVarietyModal(true);
                    }}
                    type="button"
                  >
                    + Agregar Tipo
                  </button>
                ) : null
              }
              emptyStateLabel="No hay productos disponibles."
            />
          )}
        </div>
      </div>

      {/* Modal */}
      {modalVariety && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
          onClick={handleModalClose}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Registrar compra
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  {modalVariety.productName}
                </h2>
              </div>
              <button
                onClick={handleModalClose}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Last Stock Info */}
            {lastStockItems[modalVariety.id] && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
                <p className="text-sm text-slate-700">
                  Última Compra (
                  {new Date(
                    lastStockItems[modalVariety.id].createdAt
                  ).toLocaleDateString()}
                  ) - Precio: ${lastStockItems[modalVariety.id].price},
                  Cantidad: {lastStockItems[modalVariety.id].quantity}
                </p>
              </div>
            )}
            {/* Quantity and Price Inputs */}
            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">
                  Cantidad
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="border border-gray-300 rounded p-2 w-full"
                  disabled={!canCreateStock}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Precio</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={stockPrice}
                  onChange={(e) => setStockPrice(e.target.value)}
                  className="border border-gray-300 rounded p-2 w-full"
                  disabled={!canCreateStock}
                />
              </div>
            </div>
            {/* Metric Unit Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Unidad Métrica
              </label>
              <select
                value={metricUnit}
                onChange={(e) => handleMetricUnitChange(e.target.value)}
                className="border border-gray-300 rounded p-2 w-full"
                required
                disabled={!canCreateStock}
              >
                <option value="">Selecciona una unidad</option>
                <option value="gamela">Gamela</option>
                <option value="balde">Balde</option>
                <option value="granel">Granel</option>
                <option value="bin">Bin</option>
                <option value="malla">Malla</option>
                <option value="cajonMadera">Cajón de madera</option>
                <option value="caja">Caja</option>
                <option value="saco">Saco</option>
                <option value="cajonNegro">Cajón Negro</option>
                <option value="cajonBlanco">Cajón Blanco</option>
              </select>
            </div>

            {isBinUnit(metricUnit) && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Bultos por bin
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  value={bultosPerBin}
                  onChange={(e) => setBultosPerBin(e.target.value)}
                  className="border border-gray-300 rounded p-2 w-full"
                  disabled={!canCreateStock}
                />
              </div>
            )}

            {/* Weight */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Peso (kg)
              </label>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="border border-gray-300 rounded p-2 w-full"
                disabled={!canCreateStock}
              />
            </div>

            {/* Provider */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Proveedor
              </label>
              <div className="flex gap-2">
                <div className="relative w-full" ref={providerSelectRef}>
                  <button
                    type="button"
                    onClick={() => setIsProviderSelectOpen((prev) => !prev)}
                    className="border border-gray-300 rounded p-2 w-full text-left bg-white flex items-center justify-between"
                    aria-haspopup="listbox"
                    aria-expanded={isProviderSelectOpen}
                    disabled={providerSelectDisabled}
                  >
                    <span className={selectedProvider ? "text-gray-900" : "text-gray-500"}>
                      {selectedProviderLabel}
                    </span>
                    <span className="text-gray-500">▾</span>
                  </button>
                  {isProviderSelectOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg">
                      <div className="p-2 border-b border-gray-100">
                        <input
                          type="text"
                          placeholder="Buscar proveedor..."
                          value={providerSearchTerm}
                          onChange={(e) => setProviderSearchTerm(e.target.value)}
                          className="border border-gray-300 rounded p-2 w-full"
                          disabled={providerSelectDisabled}
                        />
                      </div>
                      <ul className="max-h-60 overflow-y-auto" role="listbox">
                        {filteredProviders.length === 0 ? (
                          <li className="px-3 py-2 text-sm text-gray-500">
                            {providerSearchTerm ? "Sin resultados" : "No hay proveedores"}
                          </li>
                        ) : (
                          filteredProviders.map((provider) => {
                            const isSelected =
                              String(provider.id) === selectedProviderKey;
                            return (
                              <li key={provider.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedProviderId(provider.id);
                                    setProviderSearchTerm("");
                                    setIsProviderSelectOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                                    isSelected ? "bg-gray-100 font-medium" : ""
                                  }`}
                                  role="option"
                                  aria-selected={isSelected}
                                >
                                  <span>{provider.name}</span>
                                  {(provider.rut || provider.giro) && (
                                    <span className="block text-xs text-gray-500">
                                      {[provider.rut, provider.giro]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </span>
                                  )}
                                </button>
                              </li>
                            );
                          })
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                {canCreateProvider && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsProviderSelectOpen(false);
                      setShowProviderModal(true);
                    }}
                    className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600"
                  >
                    + Nuevo
                  </button>
                )}
              </div>
              {!canReadProvider && (
                <p className="text-xs text-gray-500 mt-2">
                  No tienes permisos para ver proveedores.
                </p>
              )}
            </div>

            {/* Payments */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium">Pagos</label>
                {canCreateStock && (
                  <button
                    type="button"
                    onClick={handleAddPaymentRow}
                    className="text-sm bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                  >
                    + Agregar pago
                  </button>
                )}
              </div>
              <div className="text-xs text-gray-600 mb-2">
                Total: ${safeTotalAmount.toLocaleString("de-DE")} | Pagado: ${paidAmount.toLocaleString("de-DE")} | Pendiente: ${balanceAmount.toLocaleString("de-DE")}
              </div>
              {overpayAmount > 0 && (
                <p className="text-xs text-red-600 mb-2">
                  Estas pagando de mas por ${overpayAmount.toLocaleString("de-DE")}.
                </p>
              )}
              {payments.length === 0 ? (
                <p className="text-sm text-gray-600">Sin pagos registrados.</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment, index) => {
                    const runningTotal = payments
                      .slice(0, index + 1)
                      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
                    const remainingAfter = Math.max(safeTotalAmount - runningTotal, 0);
                    const overpayAfter = Math.max(runningTotal - safeTotalAmount, 0);

                    return (
                    <div
                      key={`payment-${index}`}
                      className="border border-gray-200 rounded p-3 bg-gray-50"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input
                          type="number"
                          placeholder="Monto"
                          value={payment.amount}
                          onChange={(e) =>
                            handlePaymentChange(index, "amount", e.target.value)
                          }
                          className="border border-gray-300 rounded p-2 w-full"
                          disabled={!canCreateStock}
                        />
                        <select
                          value={payment.method}
                          onChange={(e) =>
                            handlePaymentChange(index, "method", e.target.value)
                          }
                          className="border border-gray-300 rounded p-2 w-full"
                          disabled={!canCreateStock}
                        >
                          <option value="">Metodo</option>
                          {paymentMethods.map((method) => (
                            <option key={method.id} value={method.id}>
                              {method.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Referencia"
                          value={payment.reference}
                          onChange={(e) =>
                            handlePaymentChange(
                              index,
                              "reference",
                              e.target.value
                            )
                          }
                          className="border border-gray-300 rounded p-2 w-full"
                          disabled={!canCreateStock}
                        />
                        <input
                          type="date"
                          value={payment.paidAt}
                          onChange={(e) =>
                            handlePaymentChange(index, "paidAt", e.target.value)
                          }
                          className="border border-gray-300 rounded p-2 w-full"
                          disabled={!canCreateStock}
                        />
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Comprobante (opcional)
                          </label>
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) =>
                              handlePaymentVoucherChange(
                                index,
                                e.target.files?.[0] || null
                              )
                            }
                            className="border border-gray-300 rounded p-2 w-full"
                            disabled={!canCreateStock}
                          />
                          {payment.voucherFile?.name && (
                            <p className="text-xs text-gray-500 mt-1">
                              {payment.voucherFile.name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mt-2">
                        Restante despues de este pago: ${remainingAfter.toLocaleString("de-DE")}
                      </div>
                      {overpayAfter > 0 && (
                        <div className="text-xs text-red-600 mt-1">
                          Estas pagando de mas por ${overpayAfter.toLocaleString("de-DE")}.
                        </div>
                      )}
                      <div className="flex justify-end mt-2">
                        {canCreateStock && (
                          <button
                            type="button"
                            onClick={() => handleRemovePaymentRow(index)}
                            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>

            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <button
                onClick={handleModalClose}
                className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                type="button"
              >
                Cancelar
              </button>
              {canCreateStock && (
                <button
                  onClick={handleSave}
                  disabled={isSavingStock}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                  type="button"
                >
                  {isSavingStock ? <InlineLoading label="Guardando" /> : "Guardar"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {showNewVarietyModal && canUpdateProduct && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
          onClick={() => setShowNewVarietyModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nuevo tipo
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Agregar tipo
                </h2>
              </div>
              <button
                onClick={() => setShowNewVarietyModal(false)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Image Upload */}
              <div className="flex justify-center">
                <div className="relative w-[150px] h-[150px]">
                  <img
                    src={newVarietyImage || "https://placehold.co/150"}
                    alt="Variety"
                    className="absolute inset-0 w-full h-full object-cover rounded-xl border border-slate-200"
                  />
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                className="border border-slate-300 rounded-lg p-2 w-full"
                onChange={(e) => {
                  setNewVarietyImage(URL.createObjectURL(e.target.files[0]));
                  setNewVarietyFile(e.target.files[0]);
                }}
              />

              {/* Variety Name Input */}
              <label className="block text-sm font-medium text-slate-700">
                Nombre del tipo de producto
              </label>
              <input
                type="text"
                placeholder="Ej: Ají jalapeño"
                className="border border-slate-300 rounded-lg p-2 w-full"
                value={newVarietyName}
                onChange={(e) => setNewVarietyName(e.target.value)}
              />

              {/* Variety Description Input */}
              <label className="block text-sm font-medium text-slate-700">
                Descripción
              </label>
              <textarea
                placeholder="Ej: Ají Jalapeño, comprado en balde."
                className="border border-slate-300 rounded-lg p-2 w-full"
                value={newVarietyDescription}
                onChange={(e) => setNewVarietyDescription(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <button
                onClick={() => setShowNewVarietyModal(false)}
                className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                type="button"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleCreateVariety()}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                type="button"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
      {showProviderModal && canCreateProvider && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
          onClick={handleProviderModalClose}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nuevo proveedor
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Registrar proveedor
                </h2>
              </div>
              <button
                onClick={handleProviderModalClose}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSaveProvider} className="space-y-4 px-6 py-5">
              {providerError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                  {providerError}
                </div>
              )}
              <ProviderFormFields
                form={providerForm}
                onChange={handleProviderFormChange}
                onRutChange={handleProviderRutChange}
                accounts={providerAccounts}
                onAccountAdd={handleAddProviderAccount}
                onAccountRemove={handleRemoveProviderAccount}
                onAccountChange={handleProviderAccountChange}
                formatRut={formatRut}
                disabled={!canCreateProvider}
                showProviderTitle={false}
                showAccountsTitle
                accountsTitle="Cuentas bancarias"
                accountsTitleClassName="font-semibold mb-2"
                containerClassName="space-y-4"
              />
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 -mx-6 px-6 py-4">
                <button
                  type="button"
                  onClick={handleProviderModalClose}
                  className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSavingProvider}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  {isSavingProvider ? <InlineLoading label="Guardando" /> : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {activeForcedVariety && canUpdateProduct && (
        <PriceItem
          id={activeForcedVariety.id}
          name={activeForcedVariety.varietyName}
          productImage={activeForcedVariety.varietyImage}
          prices={activeForcedVariety.VarietyPrices || []}
          description={activeForcedVariety.description}
          priceAutoRuleOverride={activeForcedVariety.priceAutoRuleOverride || null}
          barcodeScope={forcedProduct?.barcodeScope}
          allVarieties={forcedProduct?.Varieties || []}
          lastStockItem={lastStockItems[activeForcedVariety.id]}
          onVarietyUpdate={handleForcedVarietyUpdate}
          canUpdateProduct={canUpdateProduct}
          hideCard
          forceOpen
          onRequestClose={closeForcedPriceUpdate}
          defaultEditOpen
          hideDeleteVariety
          modalZIndexClass="z-50"
        />
      )}
    </>
  );
};

export default StockCreatePage;
